// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Providing and sharing, § API contract
// ==============================================
// The crew member's end-date choice on the Share screen (Crew-5): "we arrive",
// the skipper's suggestion, or a date they pick — plus an optional different
// date for any one paper. Pure, so the choice → payload rule is tested once.
// POST /requests/:rid/me/share takes { expiresAt?, perItem? } as calendar dates;
// the API ends access at the local midnight after each.
// ==============================================

import type { CrewItemView, CrewRequest } from './types';
import { addDays, daysBetween, fmtDay, firstName, isDate } from './dates';

/** The API refuses an end more than 400 days away (GRANT_MAX_DAYS); stay a day inside it. */
export const MAX_ACCESS_DAYS = 399;

export interface ShareOption { kind: 'arrival' | 'suggestion'; date: string; label: string; sublabel?: string }
export type ShareChoice = { kind: 'arrival' } | { kind: 'suggestion' } | { kind: 'date'; date: string };

export function shareOptions(req: CrewRequest): ShareOption[] {
  const who = `${firstName(req.requesterName)}’s suggestion`;
  const out: ShareOption[] = [];
  const suggestion = isDate(req.suggestedAccessEnd) ? req.suggestedAccessEnd : null;
  if (isDate(req.arriveOn)) {
    out.push({ kind: 'arrival', date: req.arriveOn, label: 'We arrive', ...(suggestion === req.arriveOn ? { sublabel: who } : {}) });
  }
  if (suggestion && suggestion !== req.arriveOn) {
    const after = isDate(req.arriveOn) ? daysBetween(req.arriveOn, suggestion) : 0;
    out.push({ kind: 'suggestion', date: suggestion, label: after > 0 ? `${after} days after` : `Until ${fmtDay(suggestion)}`, sublabel: who });
  }
  return out;
}

export function defaultShareChoice(options: ShareOption[]): ShareChoice {
  if (options.some((o) => o.kind === 'suggestion')) return { kind: 'suggestion' };
  if (options.some((o) => o.kind === 'arrival')) return { kind: 'arrival' };
  return { kind: 'date', date: '' };
}

export function resolveShareDate(choice: ShareChoice, options: ShareOption[]): string {
  if (choice.kind === 'date') return choice.date;
  return options.find((o) => o.kind === choice.kind)?.date ?? '';
}

export const maxEndDate = (todayDate: string) => addDays(todayDate, MAX_ACCESS_DAYS);

/** A message to show, or null when the end date is fine. */
export function validateEndDate(date: string, todayDate: string): string | null {
  if (!isDate(date)) return 'Choose an end date';
  if (date < todayDate) return 'Choose today or later';
  if (date > maxEndDate(todayDate)) return 'Choose a date within 400 days';
  return null;
}

/** Papers ready to share: answered with one of my papers, not "not needed", not shared right now. */
export function shareableItems(items: CrewItemView[]): CrewItemView[] {
  return items.filter((i) => i.paper && i.state !== 'not_needed' && !i.grant?.active);
}

export type SharePayload =
  | { ok: true; body: { expiresAt: string; perItem?: Record<string, string> } }
  | { ok: false; error: string; itemId: string | null };

export function sharePayload(choice: ShareChoice, options: ShareOption[], overrides: Record<string, string>, todayDate: string): SharePayload {
  const expiresAt = resolveShareDate(choice, options);
  const err = validateEndDate(expiresAt, todayDate);
  if (err) return { ok: false, error: err, itemId: null };
  const perItem: Record<string, string> = {};
  for (const [itemId, date] of Object.entries(overrides)) {
    if (!date) continue;
    const e = validateEndDate(date, todayDate);
    if (e) return { ok: false, error: e, itemId };
    if (date !== expiresAt) perItem[itemId] = date;
  }
  return { ok: true, body: Object.keys(perItem).length ? { expiresAt, perItem } : { expiresAt } };
}
